import React, { useEffect, useRef, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { BookOpenIcon, CalendarIcon, HomeIcon, CheckIcon, PlusIcon, XIcon } from 'lucide-react';
import { useLanguage } from '../../contexts/LanguageContext';
import { AGE_BRACKETS, DEPARTMENTS, RESIDENCE_OPTIONS } from '../../data/communications';
import type { AudienceState } from '../../types/composer';
type FilterKey = 'age' | 'service' | 'residence';
type Localized = {
  fr: string;
  en: string;
};
interface AudienceFiltersProps {
  audience: AudienceState;
  onChange: (patch: Partial<AudienceState>) => void;
}
const FILTER_META: Record<FilterKey, {
  icon: typeof HomeIcon;
  label: Localized;
}> = {
  age: {
    icon: CalendarIcon,
    label: {
      fr: "Tranche d'âge",
      en: 'Age bracket'
    }
  },
  service: {
    icon: BookOpenIcon,
    label: {
      fr: 'Abonnés au service',
      en: 'Service subscribers'
    }
  },
  residence: {
    icon: HomeIcon,
    label: {
      fr: 'Résidence',
      en: 'Residence'
    }
  }
};
const ORDER: FilterKey[] = ['age', 'service', 'residence'];

/**
 * "Qui" — filters are hidden until added, so the block stays light.
 * Each added filter narrows the audience (they combine with AND).
 */
export function AudienceFilters({
  audience,
  onChange
}: AudienceFiltersProps) {
  const {
    t,
    lang
  } = useLanguage();
  const [added, setAdded] = useState<FilterKey[]>([]);
  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);
  const isActive = (key: FilterKey) => key === 'age' ? audience.selectedAges.length > 0 : key === 'service' ? audience.selectedDepartments.length > 0 : audience.residence !== 'all';

  // A filter is shown if the user added it, or if it already restricts (e.g. ATNA / edit).
  const shown = ORDER.filter(k => added.includes(k) || isActive(k));
  const available = ORDER.filter(k => !shown.includes(k));
  useEffect(() => {
    if (!menuOpen) return;
    const onDown = (e: MouseEvent) => {
      if (!menuRef.current?.contains(e.target as Node)) setMenuOpen(false);
    };
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && setMenuOpen(false);
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [menuOpen]);
  const removeFilter = (key: FilterKey) => {
    setAdded(prev => prev.filter(k => k !== key));
    if (key === 'age') onChange({
      selectedAges: []
    });
    if (key === 'service') onChange({
      selectedDepartments: []
    });
    if (key === 'residence') onChange({
      residence: 'all'
    });
  };
  const toggle = (list: string[], id: string) => list.includes(id) ? list.filter(x => x !== id) : [...list, id];
  const chip = (active: boolean) => `flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm font-medium border transition-colors ${active ? 'bg-indigo-50 border-[#4F46E5] text-[#4F46E5]' : 'bg-white border-slate-200 text-slate-600 hover:bg-slate-50'}`;
  const renderOptions = (key: FilterKey) => {
    if (key === 'age') {
      return AGE_BRACKETS.map(a => {
        const active = audience.selectedAges.includes(a.id);
        return <button key={a.id} type="button" aria-pressed={active} onClick={() => onChange({
          selectedAges: toggle(audience.selectedAges, a.id)
        })} className={chip(active)} data-id="element-3749" data-mp="1ou8ukdprs4rp">
            {active && <CheckIcon className="w-3.5 h-3.5" data-id="element-3750" data-mp="2lj821qij52iu" />}
            {a.label[lang]}
          </button>;
      });
    }
    if (key === 'service') {
      return DEPARTMENTS.map(d => {
        const active = audience.selectedDepartments.includes(d.id);
        return <button key={d.id} type="button" aria-pressed={active} onClick={() => onChange({
          selectedDepartments: toggle(audience.selectedDepartments, d.id)
        })} className={chip(active)} data-id="element-3751" data-mp="2zihjetih22oe">
            {active && <CheckIcon className="w-3.5 h-3.5" data-id="element-3752" data-mp="1gii9z6sunoki" />}
            {d.label[lang]}
          </button>;
      });
    }
    return RESIDENCE_OPTIONS.filter(r => r.value !== 'all').map(r => {
      const active = audience.residence === r.value;
      return <button key={r.value} type="button" aria-pressed={active} onClick={() => onChange({
        residence: r.value
      })} className={chip(active)} data-id="element-3753" data-mp="12meqo8jil0mt">
          {active && <CheckIcon className="w-3.5 h-3.5" data-id="element-3754" data-mp="1ud1f3q06h66i" />}
          {r.label[lang]}
        </button>;
    });
  };
  return <div className="space-y-3" data-id="element-3755" data-mp="1pjggpwuoaa6s">
      <AnimatePresence initial={false} data-id="element-3756" data-mp="1lkhc6dcusffv">
        {shown.map(key => {
        const Meta = FILTER_META[key];
        const Icon = Meta.icon;
        return <motion.div key={key} initial={{
          opacity: 0,
          height: 0
        }} animate={{
          opacity: 1,
          height: 'auto'
        }} exit={{
          opacity: 0,
          height: 0
        }} transition={{
          duration: 0.15
        }} className="overflow-hidden" data-id="element-3757" data-mp="2i3opqj1ha9wr">
              <div className="grid grid-cols-1 md:grid-cols-[170px_1fr_auto] gap-2 md:gap-3 items-start rounded-lg border border-slate-200 p-3" data-id="element-3758" data-mp="4tf5n8ia7mv5">
                <span className="flex items-center gap-2 text-sm font-medium text-slate-700 md:pt-1.5" data-id="element-3759" data-mp="dtbo2se2vphr">
                  <Icon className="w-4 h-4 text-[#4F46E5]" data-id="element-3760" data-mp="iqaeazumfcao" /> {Meta.label[lang]}
                </span>
                <div className="flex flex-wrap gap-2" data-id="element-3761" data-mp="np5ar4ligzd9">{renderOptions(key)}</div>
                <button type="button" onClick={() => removeFilter(key)} aria-label={`${t('Retirer le filtre', 'Remove filter')} ${Meta.label[lang]}`} className="justify-self-end p-1.5 rounded-md text-slate-400 hover:text-red-600 hover:bg-red-50 transition-colors" data-id="element-3762" data-mp="sgwq0iz7vio3">
                  <XIcon className="w-4 h-4" data-id="element-3763" data-mp="16etmlfdo7p2r" />
                </button>
              </div>
            </motion.div>;
      })}
      </AnimatePresence>

      {available.length > 0 && <div className="relative w-fit" ref={menuRef} data-id="element-3764" data-mp="2xiwo1bta9b1y">
          <button type="button" onClick={() => setMenuOpen(o => !o)} aria-expanded={menuOpen} aria-haspopup="menu" className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-semibold text-[#4F46E5] rounded-lg hover:bg-indigo-50 transition-colors" data-id="element-3765" data-mp="3ca5nsdqwgcst">
            <PlusIcon className="w-4 h-4" data-id="element-3766" data-mp="3h8p8katrbijl" />
            {t('Ajouter un filtre', 'Add a filter')}
          </button>
          <AnimatePresence data-id="element-3767" data-mp="3v6nhh0pb6mrp">
            {menuOpen && <motion.ul role="menu" initial={{
          opacity: 0,
          y: -4
        }} animate={{
          opacity: 1,
          y: 0
        }} exit={{
          opacity: 0,
          y: -4
        }} transition={{
          duration: 0.12
        }} className="absolute left-0 z-30 mt-1 w-56 bg-white border border-slate-200 rounded-lg shadow-lg py-1" data-id="element-3768" data-mp="3qaor57kf6uon">
                {available.map(key => {
            const Icon = FILTER_META[key].icon;
            return <li key={key} data-id="element-3769" data-mp="1n0ho3lhnthd">
                      <button type="button" role="menuitem" onClick={() => {
                setAdded(prev => [...prev, key]);
                setMenuOpen(false);
              }} className="w-full flex items-center gap-2 px-3 py-2 text-sm text-slate-700 hover:bg-indigo-50 text-left" data-id="element-3770" data-mp="1lpdw3s1mlq9">
                        <Icon className="w-4 h-4 text-slate-400" data-id="element-3771" data-mp="3jsvciiidf905" />
                        {FILTER_META[key].label[lang]}
                      </button>
                    </li>;
          })}
              </motion.ul>}
          </AnimatePresence>
        </div>}
    </div>;
}